import React, { useEffect, useState } from 'react';
import {
  COUNTDOWN_MONO_FAMILY,
  usePublicCountdownTarget,
  resolveLiveCountdownTarget,
  useCountdown,
} from './countdown';
import { useEditorPreview } from './EditorPreviewContext';
import type { PromoTicketShape } from './promoTicket';
import type { TextRoleStyle } from './semanticTextRoles';

/**
 * WEB-926, позиция 4 — промо-тикет (референсы 12min и BetterMe: плашка «код
 * применён», перфорация по краю, моно-код, таймер).
 *
 * ГРАНИЦА ОТВЕТСТВЕННОСТИ (решение PM 2026-07-31). Это ОФОРМЛЕНИЕ, а не
 * механика купонов: в системе нет скидок, чекаут создаётся без `discounts`, и
 * блок ничего не применяет к заказу. Цены здесь — строки автора (у него тариф
 * $10 — он сам пишет $5), а реальная сумма списания показывается пользователю
 * окном Stripe на оплате. Поэтому блок сознательно НЕ утверждает от себя, что
 * скидка применена автоматически: любую формулировку пишет автор.
 *
 * Таймер — общий модуль `countdown.ts` (WEB-925), второго отсчёта не заводим:
 * якорь в sessionStorage по id блока, перезагрузка не сбрасывает срок.
 *
 * WEB-978 п.6: в ПРЕВЬЮ редактора якорь по blockId — ловушка, а не фича: тот
 * же id живёт, пока автор работает над воронкой, и просроченный якорь стопорит
 * отсчёт на 00:00:00 навсегда (и смена «Срок, минут» не лечит, потому что
 * useState-инициализатор больше не перевызывается). В превью
 * (isEditorPreview) считаем живой отсчёт от «сейчас» через
 * resolveLiveCountdownTarget — sessionStorage не трогаем вовсе. Публика
 * (isEditorPreview=false) не меняется байт-в-байт.
 */

interface PromoTicketBlockProps {
  blockId?: string;
  shape: PromoTicketShape;
  title?: string;
  code?: string;
  note?: string;
  oldPrice?: string;
  newPrice?: string;
  showTimer?: boolean;
  targetDate?: string;
  durationMinutes?: number;
  /** Локализованная подпись у таймера (что именно истекает). */
  timerLabel: string;
  /**
   * WEB-1692 юнит 3 (репорт владельца 24.08 «не хватает раздельных настроек
   * для заголовка тикета, кода и самого таймера — именно настройка размера и
   * цвета»). Накладки спредятся ПОСЛЕ прежних строк и при пустых полях равны
   * пустому объекту: старая воронка не меняется ни на байт.
   *
   * ⚠ ЦЕН ЗДЕСЬ НЕТ НАМЕРЕННО. Старая зачёркнутая и новая акцентная работают
   * ПАРОЙ «было/стало»: настроить половину пары нельзя, а про обе владелец не
   * просил. Понадобится — своя пара полей `promoTicketPrice*`, не эти.
   */
  titleStyle?: TextRoleStyle;
  noteStyle?: TextRoleStyle;
  codeStyle?: TextRoleStyle;
  timerStyle?: TextRoleStyle;
}

function pad(value: number): string {
  return String(value).padStart(2, '0');
}

/** Компактная моно-строка: дни показываем только когда они есть. */
function formatTicketTime(time: {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
}): string {
  const core = `${pad(time.hours)}:${pad(time.minutes)}:${pad(time.seconds)}`;
  return time.days > 0 ? `${time.days}:${core}` : core;
}

const NOTCH_SIZE = 18;

export function PromoTicketBlock({
  blockId,
  shape,
  title,
  code,
  note,
  oldPrice,
  newPrice,
  showTimer,
  targetDate,
  durationMinutes,
  timerLabel,
  titleStyle,
  noteStyle,
  codeStyle,
  timerStyle,
}: PromoTicketBlockProps): React.ReactElement {
  const withTimer = showTimer !== false;
  const { isEditorPreview, frozen } = useEditorPreview();
  const [liveTargetMs, setTargetMs] = useState<number | null>(() => {
    if (!withTimer || !isEditorPreview) return null;
    return resolveLiveCountdownTarget(targetDate, durationMinutes);
  });
  // WEB-3673: публика — якорь от показа человеку (прогретая SDK страница
  // `?preload=1` до показа стоит на полном сроке и якорь не пишет).
  const publicTarget = usePublicCountdownTarget(
    blockId,
    targetDate,
    durationMinutes,
    withTimer && !isEditorPreview,
  );
  const targetMs = isEditorPreview ? liveTargetMs : publicTarget.targetMs;
  // WEB-978 п.6: только превью пересчитывает — публика держит один и тот же
  // targetMs на весь жизненный цикл компонента (якорь sessionStorage читается
  // ровно один раз, лениво, из useState выше). В превью же смена «Срок,
  // минут» / targetDate обязана двигать отсчёт, а не застревать на первом
  // значении, с которым блок был смонтирован.
  useEffect(() => {
    if (!isEditorPreview) return;
    setTargetMs(withTimer ? resolveLiveCountdownTarget(targetDate, durationMinutes) : null);
  }, [isEditorPreview, withTimer, targetDate, durationMinutes]);
  // WEB-2996: замороженный рендер (узел графа) — таймер стоит.
  const time = useCountdown(
    withTimer ? targetMs : null,
    frozen || publicTarget.waiting,
  );
  const perforated = shape === 'perforated';
  const accent = 'var(--accent-money, var(--color-success, #16a34a))';

  const notchStyle: React.CSSProperties = {
    position: 'absolute',
    top: -NOTCH_SIZE / 2,
    width: NOTCH_SIZE,
    height: NOTCH_SIZE,
    borderRadius: '50%',
    // Полукруги «вырезаны» цветом фона страницы — как у бумажного билета.
    background: 'var(--color-bg-primary, #fff)',
  };

  return (
    <div
      data-testid="promo-ticket"
      data-shape={shape}
      style={{
        position: 'relative',
        overflow: 'hidden',
        marginBottom: 16,
        borderRadius: 'var(--radius-card, var(--border-radius-lg, 12px))',
        border: `1px solid ${accent}`,
        background: 'var(--color-bg-surface, #f6ffed)',
      }}
    >
      <div style={{ padding: '14px 16px', textAlign: 'center' }}>
        {title && (
          <div
            data-testid="promo-ticket-title"
            // WEB-1692: накладка автора — ПОСЛЕ прежних строк; пусто = {}.
            // Денежный акцент остаётся умолчанием заголовка и рамки.
            style={{ fontSize: 14, fontWeight: 600, color: accent, ...(titleStyle ?? {}) }}
          >
            {title}
          </div>
        )}
        {code && (
          <div
            data-testid="promo-ticket-code"
            style={{
              marginTop: title ? 8 : 0,
              fontFamily: COUNTDOWN_MONO_FAMILY,
              fontSize: 20,
              fontWeight: 700,
              letterSpacing: 1,
              // WEB-1440 (класс WEB-966): всё это лежит на карточке-билете.
              color: 'var(--color-text-on-surface, var(--color-text-primary, rgba(0,0,0,0.88)))',
              // WEB-1692: накладка автора — ПОСЛЕ прежних строк; пусто = {}.
              // Моно-семья остаётся: по ней код и узнают.
              ...(codeStyle ?? {}),
            }}
          >
            {code}
          </div>
        )}
        {note && (
          <div
            style={{
              marginTop: 6,
              fontSize: 12,
              color: 'var(--color-text-on-surface, var(--color-text-secondary, #595959))',
              // WEB-1692: накладка роли `Secondary`; пусто = {}.
              ...(noteStyle ?? {}),
            }}
          >
            {note}
          </div>
        )}
      </div>

      <div style={{ position: 'relative' }}>
        <div
          style={{
            borderTop: perforated
              ? `2px dashed ${accent}`
              : '1px solid var(--color-border-primary, #d9d9d9)',
          }}
        />
        {perforated && (
          <>
            <span
              data-testid="promo-ticket-notch"
              aria-hidden="true"
              style={{ ...notchStyle, left: -NOTCH_SIZE / 2 }}
            />
            <span
              data-testid="promo-ticket-notch"
              aria-hidden="true"
              style={{ ...notchStyle, right: -NOTCH_SIZE / 2 }}
            />
          </>
        )}
      </div>

      {(oldPrice || newPrice || withTimer) && (
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 12,
            padding: '12px 16px',
          }}
        >
          {oldPrice && (
            <span
              data-testid="promo-ticket-old-price"
              style={{
                textDecoration: 'line-through',
                fontSize: 14,
                color: 'var(--color-text-on-surface, var(--color-text-tertiary, #8c8c8c))',
              }}
            >
              {oldPrice}
            </span>
          )}
          {newPrice && (
            <span
              data-testid="promo-ticket-new-price"
              style={{ fontSize: 18, fontWeight: 700, color: accent }}
            >
              {newPrice}
            </span>
          )}
          {withTimer && (
            <span
              data-testid="promo-ticket-timer"
              aria-label={timerLabel}
              style={{
                marginLeft: 'auto',
                marginRight: 'auto',
                fontFamily: COUNTDOWN_MONO_FAMILY,
                fontSize: 16,
                fontWeight: 600,
                color: 'var(--color-text-on-surface, var(--color-text-primary, rgba(0,0,0,0.88)))',
                // WEB-1692: накладка автора — ПОСЛЕ прежних строк; пусто = {}.
                ...(timerStyle ?? {}),
              }}
            >
              {formatTicketTime(time)}
            </span>
          )}
        </div>
      )}
    </div>
  );
}
